import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getFromServer, postToServer, putToServer, deleteFromServer } from "../../../components/Common/requests";
import { useDispatch } from "react-redux";
import { setUserTags, setAllTags } from "../../../store/tagsSlice";
import { showToast } from "@jobber/components/Toast";
import { addUniqueIds } from "../../../components/Common/Validations";

export const useUserTags = () => {
  const dispatch = useDispatch();
  return useQuery({
    queryKey: ["userTags"],
    queryFn: async () => {
      const res = await getFromServer("tags");
      if (!res.status) throw new Error(res.message || res.error);
      dispatch(setUserTags(res.data));
      return res.data;
    },
  });
};

export const useAllTags = () => {
  const dispatch = useDispatch();
  return useQuery({
    queryKey: ["allTags"],
    queryFn: async () => {
      const res = await getFromServer("Client-Tags");
      if (!res.status) throw new Error(res.message || res.error);
      const tags = addUniqueIds(res.data);
      dispatch(setAllTags(tags));
      return tags;
    },
  });
};

export const useAddCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => postToServer("tags", data),
    onSuccess: (res) => {
      if (res.status) {
        queryClient.invalidateQueries({ queryKey: ["userTags"] });
        showToast({ message: "Category added successfully", variation: "success" });
      } else {
        showToast({ message: res.message || res.error, variation: "error" });
      }
    },
  });
};

export const useUpdateCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => putToServer(`tags/${id}`, data),
    onSuccess: (res) => {
      if (res.status) {
        queryClient.invalidateQueries({ queryKey: ["userTags"] });
      } else {
        showToast({ message: res.message || res.error, variation: "error" });
      }
    },
  });
};

export const useDeleteCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteFromServer(`tags/${id}`),
    onSuccess: (res) => {
      if (res.status) {
        queryClient.invalidateQueries({ queryKey: ["userTags"] });
        showToast({ message: "Category deleted successfully", variation: "success" });
      } else {
        showToast({ message: res.message || res.error, variation: "error" });
      }
    },
  });
};

export const useRearrangeCategories = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (categories) => postToServer("Update-Category-Position", { categories }),
    onSuccess: (res) => {
      if (res.status) {
        queryClient.invalidateQueries({ queryKey: ["userTags"] });
        showToast({ message: "Categories order updated successfully" });
      } else {
        showToast({ message: res.message || res.error, variation: "error" });
      }
    },
  });
};

export const useMoveTag = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userTags, tagId, tag, source, fromCid, toCid }) => {
      if (fromCid === toCid) return;

      if (source === "All Tags") {
        const toCategory = userTags.find((c) => c._id === toCid);
        if (!toCategory) return;

        const newTags = [...toCategory.tags, tag];
        showToast({ message: "Please wait until tag added", variation: "info" });
        return putToServer(`tags/${toCid}`, { tags: newTags });
      } else {
        const fromCategory = userTags.find((c) => c._id === fromCid);
        const toCategory = userTags.find((c) => c._id === toCid);
        if (!fromCategory || !toCategory) return;

        const newFromTags = fromCategory.tags.filter((t) => t._id !== tagId);
        const tagToMove = fromCategory.tags.find((t) => t._id === tagId);
        const newToTags = [...toCategory.tags, tagToMove];

        const res1 = await putToServer(`tags/${toCid}`, { tags: newToTags });
        if (res1.status) {
          return putToServer(`tags/${fromCid}`, { tags: newFromTags });
        }
        return res1;
      }
    },
    onSuccess: (res) => {
      if (res?.status) {
        showToast({ message: "Tags moved successfully" });
      } else if (res) {
        showToast({ message: res.message || res.error, variation: "error" });
      }
      queryClient.invalidateQueries({ queryKey: ["userTags"] });
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ["userTags"] });
    }
  });
};

export const useDeleteTagFromCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ tagId, catId, userTags }) => {
      const category = userTags.find((c) => c._id === catId);
      if (!category) return;

      const newTags = category.tags.filter((t) => t._id !== tagId);
      return putToServer(`tags/${catId}`, { tags: newTags });
    },
    onSuccess: (res) => {
      if (res.status) {
        showToast({ message: "Tag deleted successfully" });
      } else {
        showToast({ message: res.message || res.error, variation: "error" });
      }
      queryClient.invalidateQueries({ queryKey: ["userTags"] });
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ["userTags"] });
    }
  });
};
